import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { LayoutGrid, Pencil, Power, PowerOff, AlertTriangle, Crown, ShieldCheck, Check } from "lucide-react";

// Admin Master — Planos, Recursos e Matriz (Gestão Comercial SaaS). Área
// provisória: catálogo de Planos/Recursos, preço PADRÃO de cada plano
// (referência para novas condições comerciais — nunca recalcula nada já
// negociado) e a matriz de associação entre eles. Preço/condição comercial
// ESPECÍFICA de cada empresa, descontos, reajustes e cobrança em si vivem em
// Gestão Comercial (menu próprio) — esta tela só administra o CATÁLOGO.
//
// O plano contratado de cada empresa e as exceções/overrides individuais
// continuam em Empresas.jsx (aba "Plano LOOTUS e Recursos" do formulário de
// edição) — esta tela só administra o CATÁLOGO consultado por lá.
const emptyPlan = { nome: "", codigo: "", descricao: "", active: true, valor_mensal_padrao: 0, valor_adesao_padrao: 0, moeda: "BRL" };
const emptyFeature = { nome: "", codigo: "", descricao: "", active: true };

// Ordem FIXA de exibição — nunca depende da ordem do Mongo/preço/data de
// criação (Ajuste de UX — Planos e Recursos, item 1). Um plano futuro
// desconhecido (fora dos 3 oficiais) cai no fim, nunca quebra a ordenação.
const PLAN_ORDER = { basic: 0, gold: 1, personalizado: 2 };
const sortPlans = (list) => [...list].sort((a, b) => (PLAN_ORDER[a.codigo] ?? 99) - (PLAN_ORDER[b.codigo] ?? 99));

// DISPLAY COMERCIAL apenas (item 2/3) — nomes reais do produto (mesmos
// rótulos de frontend/src/components/Layout.jsx), nunca gravado em
// db.features, nunca passa por require_feature. Core Universal continua
// inteiramente fora do catálogo/entitlement — isto é só a lista mostrada
// nos 3 cards para comparação comercial lado a lado.
const CORE_CAPABILITIES = [
  "Dashboard",
  "Gestão de clientes",
  "Fichas de serviço",
  "Aprovações",
  "Assinatura digital de fichas",
  "Relatórios",
  "Gestão de usuários e acessos",
];


export default function Planos() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [planOpen, setPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);

  const [featureOpen, setFeatureOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureForm, setFeatureForm] = useState(emptyFeature);

  const [matrixWarning, setMatrixWarning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: plansData }, { data: featuresData }] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/features"),
      ]);
      setPlans(plansData);
      setFeatures(featuresData);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Não existe mais "criar plano" nesta tela (correção de escopo — as
  // modalidades oficiais atuais são só Básico/Gold/Personalizado; a rota
  // POST /admin/plans continua existindo no backend, como mecanismo
  // técnico extensível para o futuro, mas deliberadamente sem gatilho na
  // interface agora). Só edição de nome/descrição/preço/situação de um
  // plano já existente.
  const openEditPlan = (p) => { setPlanForm({ ...p }); setEditingPlan(p); setPlanOpen(true); };
  const submitPlan = async () => {
    if (!editingPlan) return;
    if (!planForm.nome.trim()) return toast.error("Informe o nome do plano");
    if (planForm.valor_mensal_padrao < 0 || planForm.valor_adesao_padrao < 0) return toast.error("Valores não podem ser negativos");
    try {
      await api.put(`/admin/plans/${editingPlan.id}`, {
        nome: planForm.nome, descricao: planForm.descricao, active: planForm.active,
        valor_mensal_padrao: Number(planForm.valor_mensal_padrao) || 0,
        valor_adesao_padrao: Number(planForm.valor_adesao_padrao) || 0,
        moeda: planForm.moeda || "BRL",
      });
      toast.success("Plano atualizado");
      setPlanOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePlanActive = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, {
        nome: p.nome, descricao: p.descricao, active: !p.active,
        valor_mensal_padrao: p.valor_mensal_padrao, valor_adesao_padrao: p.valor_adesao_padrao, moeda: p.moeda,
      });
      toast.success(p.active ? "Plano desativado" : "Plano ativado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Não existe mais "criar recurso" (Consolidação Comercial, item 1) — só
  // edição de nome/descrição/situação de um recurso já existente do
  // catálogo oficial (OFFICIAL_FEATURE_CATALOG, backend). POST
  // /admin/features foi removido.
  const openEditFeature = (f) => { setFeatureForm({ ...f }); setEditingFeature(f); setFeatureOpen(true); };
  const submitFeature = async () => {
    if (!editingFeature) return;
    if (!featureForm.nome.trim()) return toast.error("Informe o nome do recurso");
    try {
      await api.put(`/admin/features/${editingFeature.id}`, {
        nome: featureForm.nome, descricao: featureForm.descricao, active: featureForm.active,
      });
      toast.success("Recurso atualizado");
      setFeatureOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleFeatureActive = async (f) => {
    try {
      await api.put(`/admin/features/${f.id}`, { nome: f.nome, descricao: f.descricao, active: !f.active });
      toast.success(f.active ? "Recurso desativado" : "Recurso ativado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Matriz Plano×Recurso — item 4: marcar/desmarcar cada célula
  // individualmente por plano, nunca um travamento rígido entre planos
  // (o backend só devolve um AVISO quando o Básico tem algo que o Gold não
  // tem, nunca corrige nada sozinho).
  const toggleMatrixCell = async (plan, feature, checked) => {
    try {
      const { data } = checked
        ? await api.post(`/admin/plans/${plan.id}/features/${feature.codigo}`)
        : await api.delete(`/admin/plans/${plan.id}/features/${feature.codigo}`);
      setMatrixWarning(data.warning || null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><LayoutGrid size={28} /> Planos e Recursos</h1>
        <p className="text-slate-500 text-sm">Catálogo de planos, recursos do sistema e a matriz de associação entre eles</p>
      </div>

      <Tabs defaultValue="planos">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="planos" data-testid="tab-planos">Planos</TabsTrigger>
          <TabsTrigger value="recursos" data-testid="tab-recursos">Recursos do sistema</TabsTrigger>
          <TabsTrigger value="matriz" data-testid="tab-matriz">Matriz Plano × Recurso</TabsTrigger>
        </TabsList>

        <TabsContent value="planos" className="mt-4 space-y-4">
          <div>
            <p className="text-sm text-slate-500">Cards de apresentação comercial dos planos — o preço aqui é o PADRÃO; cada empresa pode ter uma condição específica em Gestão Comercial.</p>
            <p className="text-xs text-slate-400 mt-1">
              As modalidades oficiais atuais são Básico, Gold e Personalizado — não é possível criar um novo plano comercial por
              aqui neste momento. Você ainda pode editar nome/descrição/preço/situação de um plano já existente.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            {sortPlans(plans).map(p => {
              const destaque = p.codigo === "gold";
              const personalizado = p.codigo === "personalizado";
              // Card mostra SÓ os recursos realmente associados ao plano —
              // mesma fonte da Matriz Plano×Recurso (p.feature_codes), nunca
              // uma lista visual separada. Um recurso marcado para o plano
              // aparece aqui imediatamente; desmarcado, desaparece — sem
              // exceção hardcoded (correção do bug em que "Emissão de
              // NFS-e" ficava marcada na Matriz para Gold mas nunca
              // aparecia no card, por causa de uma lista de exclusão fixa
              // que existia aqui antes). Personalizado não tem composição
              // fixa (matriz é só informativa para ele — "definido por
              // empresa"), então continua mostrando todos os recursos
              // ativos como opcionais.
              const activeFeatures = features.filter(f => f.active);
              const planFeatures = personalizado
                ? activeFeatures
                : activeFeatures.filter(f => (p.feature_codes || []).includes(f.codigo));
              return (
                <Card key={p.id} className={`p-5 relative overflow-hidden flex flex-col h-full ${destaque ? "border-2 border-amber-300" : "border border-slate-200"} ${!p.active ? "opacity-60" : ""}`} data-testid={`plano-card-${p.codigo}`}>
                  {destaque && (
                    <div className="absolute top-0 right-0 bg-amber-400 text-amber-950 text-[10px] font-bold tracking-wide px-3 py-1 rounded-bl-lg">
                      MAIS COMPLETO
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    {destaque ? <Crown size={18} className="text-amber-500" /> : <ShieldCheck size={18} className="text-blue-500" />}
                    <div className="font-heading text-lg font-bold text-slate-900">{p.nome}</div>
                  </div>
                  <Badge className={`w-fit mb-2 ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{p.active ? "Ativo" : "Inativo"}</Badge>
                  <p className="text-sm text-slate-500 mb-3 min-h-[40px]">{p.descricao || "Sem descrição"}</p>
                  <div className="mb-4">
                    <span className="font-heading text-3xl font-black text-slate-900">R$ {(p.valor_mensal_padrao || 0).toFixed(2)}</span>
                    <span className="text-sm text-slate-500">/mês</span>
                    {p.valor_adesao_padrao > 0 && <div className="text-xs text-slate-400">+ R$ {p.valor_adesao_padrao.toFixed(2)} de adesão</div>}
                  </div>

                  {/* Comparação comercial (item 2/3) — capacidades essenciais
                      são DISPLAY apenas (nunca db.features/require_feature);
                      recursos premium refletem de verdade p.feature_codes. */}
                  <div className="flex-1 space-y-3 mb-4">
                    <div>
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Capacidades essenciais</div>
                      <div className="space-y-1">
                        {CORE_CAPABILITIES.map(cap => (
                          <div key={cap} className="flex items-center gap-2 text-sm text-slate-700">
                            <Check size={14} className="text-emerald-600 shrink-0" /> {cap}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Recursos premium</div>
                      <div className="space-y-1">
                        {planFeatures.map(f => (
                          personalizado ? (
                            <div key={f.codigo} className="flex items-center gap-2 text-sm text-slate-500" data-testid={`plano-card-recurso-${p.codigo}-${f.codigo}`}>
                              <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 shrink-0" />
                              {f.nome} <span className="text-xs text-slate-400 italic">— opcional</span>
                            </div>
                          ) : (
                            <div key={f.codigo} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`plano-card-recurso-${p.codigo}-${f.codigo}`}>
                              <Check size={14} className="text-emerald-600 shrink-0" /> {f.nome}
                            </div>
                          )
                        ))}
                        {!planFeatures.length && <div className="text-xs text-slate-400">Nenhum recurso premium neste plano</div>}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <Button size="sm" variant="outline" onClick={() => openEditPlan(p)} data-testid={`plano-editar-${p.id}`}><Pencil size={14} /> Editar</Button>
                    <Button size="sm" variant="outline" onClick={() => togglePlanActive(p)} data-testid={`plano-toggle-${p.id}`}>
                      {p.active ? <PowerOff size={14} /> : <Power size={14} />} {p.active ? "Inativar" : "Ativar"}
                    </Button>
                  </div>
                </Card>
              );
            })}
            {!loading && plans.length === 0 && (
              <Card className="p-8 text-center text-slate-400 lg:col-span-3">Nenhum plano cadastrado</Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recursos" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="font-heading font-semibold">Recursos do sistema</div>
              <p className="text-xs text-slate-500 mt-1">
                Catálogo OFICIAL de recursos reais do LOOTUS — registrado em código quando uma
                funcionalidade é desenvolvida, não por cadastro livre aqui. Você pode editar nome/
                descrição/situação de um recurso já existente e associá-lo aos planos (aba Matriz),
                mas não pode criar um recurso novo por este formulário.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr><th className="py-3 px-4">Nome</th><th>Código</th><th>Descrição</th><th>Situação</th><th className="text-right pr-4">Ações</th></tr>
                </thead>
                <tbody>
                  {features.map(f => (
                    <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-900">{f.nome}</td>
                      <td className="text-slate-400 font-mono text-xs">{f.codigo}</td>
                      <td className="text-slate-500">{f.descricao || "-"}</td>
                      <td><Badge className={f.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{f.active ? "Ativo" : "Inativo"}</Badge></td>
                      <td className="text-right pr-4 space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openEditFeature(f)} title="Editar" data-testid={`recurso-editar-${f.id}`}><Pencil size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleFeatureActive(f)} title={f.active ? "Inativar" : "Ativar"} data-testid={`recurso-toggle-${f.id}`}>
                          {f.active ? <PowerOff size={16} /> : <Power size={16} />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && features.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nenhum recurso cadastrado</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="matriz" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="font-heading font-semibold">Matriz Plano × Recurso</div>
              <p className="text-xs text-slate-500 mt-1">Marque em quais planos cada recurso está disponível. Normalmente tudo que está no Básico também está no Gold — isto não é obrigatório, apenas um alerta informativo aparece quando não for o caso.</p>
              <p className="text-xs text-slate-500 mt-1">
                Personalizado não tem uma composição global fixa — cada empresa Personalizado escolhe seus próprios recursos
                (Empresas → editar → Recursos contratados), por isso a coluna Personalizado aqui é só informativa.
              </p>
            </div>
            {matrixWarning && (
              <div className="mx-4 mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2" data-testid="matriz-warning">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {matrixWarning}
              </div>
            )}
            <div className="overflow-x-auto p-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="py-3 px-4">Recurso</th>
                    {sortPlans(plans).map(p => <th key={p.id} className="text-center px-3">{p.nome}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {features.map(f => (
                    <tr key={f.id} className="border-t border-slate-100">
                      <td className="py-3 px-4 font-medium text-slate-900">{f.nome}</td>
                      {sortPlans(plans).map(p => {
                        if (p.codigo === "personalizado") {
                          return (
                            <td key={p.id} className="text-center text-xs text-slate-400 italic px-2" data-testid={`matriz-${p.codigo}-${f.codigo}`}>
                              Definido por empresa
                            </td>
                          );
                        }
                        const checked = (p.feature_codes || []).includes(f.codigo);
                        return (
                          <td key={p.id} className="text-center">
                            <input
                              type="checkbox" checked={checked}
                              onChange={e => toggleMatrixCell(p, f, e.target.checked)}
                              data-testid={`matriz-${p.codigo}-${f.codigo}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {!loading && features.length === 0 && <tr><td colSpan={1 + plans.length} className="py-8 text-center text-slate-400">Nenhum recurso cadastrado</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar plano</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="plano-form-nome" value={planForm.nome} onChange={e => setPlanForm({ ...planForm, nome: e.target.value })} /></div>
            <div>
              <Label>Código interno</Label>
              <Input value={planForm.codigo || ""} disabled />
              <p className="text-xs text-slate-500 mt-1">Identificador técnico — nunca editável, nunca exibido ao usuário final.</p>
            </div>
            <div><Label>Descrição</Label><Textarea data-testid="plano-form-descricao" value={planForm.descricao || ""} onChange={e => setPlanForm({ ...planForm, descricao: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mensalidade padrão (R$)</Label>
                <Input type="number" min="0" step="0.01" data-testid="plano-form-valor-mensal" value={planForm.valor_mensal_padrao}
                  onChange={e => setPlanForm({ ...planForm, valor_mensal_padrao: e.target.value })} />
              </div>
              <div>
                <Label>Adesão padrão (R$)</Label>
                <Input type="number" min="0" step="0.01" data-testid="plano-form-valor-adesao" value={planForm.valor_adesao_padrao}
                  onChange={e => setPlanForm({ ...planForm, valor_adesao_padrao: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-slate-500">Referência para novas condições comerciais — alterar aqui nunca recalcula cobranças já geradas nem a condição específica já negociada de nenhuma empresa.</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={planForm.active} onChange={e => setPlanForm({ ...planForm, active: e.target.checked })} /> Ativo
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitPlan} data-testid="plano-form-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={featureOpen} onOpenChange={setFeatureOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar recurso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="recurso-form-nome" value={featureForm.nome} onChange={e => setFeatureForm({ ...featureForm, nome: e.target.value })} /></div>
            <div>
              <Label>Código interno</Label>
              <Input value={featureForm.codigo || ""} disabled />
              <p className="text-xs text-slate-500 mt-1">Identificador técnico do catálogo oficial — nunca editável, nunca exibido ao usuário final.</p>
            </div>
            <div><Label>Descrição</Label><Textarea data-testid="recurso-form-descricao" value={featureForm.descricao || ""} onChange={e => setFeatureForm({ ...featureForm, descricao: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={featureForm.active} onChange={e => setFeatureForm({ ...featureForm, active: e.target.checked })} /> Ativo
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeatureOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitFeature} data-testid="recurso-form-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
